/**
 * Microsoft Graph へのアクセス（仕様書 §8.5）。
 * アクセストークン取得後は BFF を経由せず、ブラウザから Graph を直接呼ぶ。
 */

import { ReauthRequiredError, tokenProvider } from './TokenProvider';

const GRAPH = 'https://graph.microsoft.com/v1.0';
/** OneDrive の App Folder。実体は OneDrive/アプリ/Necosuke Journal/ */
const APP_ROOT = '/me/drive/special/approot';

export interface GraphItem {
  id: string;
  name: string;
  eTag?: string;
  cTag?: string;
  size?: number;
  lastModifiedDateTime?: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  deleted?: { state: string };
  parentReference?: { id?: string; path?: string; driveId?: string };
}

export interface DeltaPage {
  items: GraphItem[];
  nextLink: string | null;
  deltaLink: string | null;
}

export class GraphError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'GraphError';
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: BodyInit | null;
  /** 401 での再試行を行ったかどうか（内部用）。 */
  retried?: boolean;
}

async function request(path: string, opts: RequestOptions = {}): Promise<Response> {
  const token = await tokenProvider.get();
  const url = path.startsWith('http') ? path : GRAPH + path;

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers: { Authorization: `Bearer ${token}`, ...(opts.headers ?? {}) },
    body: opts.body ?? null,
  });

  // トークンが失効していたら1回だけ取り直して再試行する（§8.4）
  if (res.status === 401 && !opts.retried) {
    tokenProvider.invalidate();
    try {
      return await request(path, { ...opts, retried: true });
    } catch (e) {
      if (e instanceof ReauthRequiredError) throw e;
      throw e;
    }
  }

  // スロットリングは Retry-After に従う（§17.3）
  if ((res.status === 429 || res.status === 503) && !opts.retried) {
    const wait = Number(res.headers.get('Retry-After') ?? '5') * 1000;
    await new Promise((r) => setTimeout(r, Math.min(wait, 60_000)));
    return request(path, { ...opts, retried: true });
  }

  return res;
}

async function toError(res: Response): Promise<GraphError> {
  const body = (await res.json().catch(() => null)) as
    | { error?: { code?: string; message?: string } }
    | null;
  return new GraphError(
    res.status,
    body?.error?.code ?? 'unknown',
    body?.error?.message ?? `Graph API エラー (${res.status})`,
  );
}

/** App Folder 内の相対パスを Graph のアドレス指定に変換する。 */
function itemPath(relPath: string): string {
  const clean = relPath.replace(/^\/+/, '');
  return clean === '' ? APP_ROOT : `${APP_ROOT}:/${clean.split('/').map(encodeURIComponent).join('/')}`;
}

export const graph = {
  /** サインイン中のアカウント情報。疎通確認にも使う。 */
  async me(): Promise<{ displayName: string; userPrincipalName: string }> {
    const res = await request('/me');
    if (!res.ok) throw await toError(res);
    return res.json();
  },

  /** App Folder のメタデータ。存在しない場合は初回アクセス時に自動生成される。 */
  async appRoot(): Promise<GraphItem> {
    const res = await request(APP_ROOT);
    if (!res.ok) throw await toError(res);
    return res.json();
  },

  /** JSONファイルを読む。存在しなければ null。 */
  async getJson<T>(relPath: string): Promise<{ data: T; eTag: string | null } | null> {
    const res = await request(`${itemPath(relPath)}:/content`);
    if (res.status === 404) return null;
    if (!res.ok) throw await toError(res);
    return { data: (await res.json()) as T, eTag: res.headers.get('ETag') };
  },

  /**
   * ファイルを書く。ifMatch を渡すと競合時に 412 を返す（§7.3）。
   *
   * 深いパスへ直接PUTすると、途中のフォルダは OneDrive 側が自動生成する。
   * App Folder に対する POST /children でのフォルダ作成は 400 になるため、
   * フォルダを明示的に作ってはならない。
   */
  async put(
    relPath: string,
    body: BodyInit,
    mime: string,
    ifMatch?: string | null,
  ): Promise<{ item: GraphItem; eTag: string | null } | 'conflict'> {
    const headers: Record<string, string> = { 'Content-Type': mime };
    if (ifMatch) headers['If-Match'] = ifMatch;
    const res = await request(`${itemPath(relPath)}:/content`, { method: 'PUT', headers, body });
    if (res.status === 412) return 'conflict';
    if (!res.ok) throw await toError(res);
    return { item: (await res.json()) as GraphItem, eTag: res.headers.get('ETag') };
  },

  async putJson(relPath: string, value: unknown, ifMatch?: string | null) {
    return graph.put(relPath, JSON.stringify(value), 'application/json', ifMatch);
  },

  /** バイナリを取得する。存在しなければ null。 */
  async getBlob(relPath: string): Promise<Blob | null> {
    const res = await request(`${itemPath(relPath)}:/content`);
    if (res.status === 404) return null;
    if (!res.ok) throw await toError(res);
    return res.blob();
  },

  /** メタデータのみ取得する（eTag の確認用）。存在しなければ null。 */
  async stat(relPath: string): Promise<GraphItem | null> {
    const res = await request(itemPath(relPath));
    if (res.status === 404) return null;
    if (!res.ok) throw await toError(res);
    return res.json();
  },

  /**
   * 差分を取得する（仕様書 §7.4）。
   * link を渡すと続き（nextLink / deltaLink）から取得する。
   * トークン失効（410）の場合は null を返し、呼び出し側でブートストラップし直す。
   */
  async delta(link?: string | null): Promise<DeltaPage | null> {
    const url = link ?? `${APP_ROOT}/delta`;
    const res = await request(url);
    if (res.status === 410) return null;
    if (!res.ok) throw await toError(res);
    const body = (await res.json()) as {
      value: GraphItem[];
      '@odata.nextLink'?: string;
      '@odata.deltaLink'?: string;
    };
    return {
      items: body.value ?? [],
      nextLink: body['@odata.nextLink'] ?? null,
      deltaLink: body['@odata.deltaLink'] ?? null,
    };
  },

  async delete(relPath: string): Promise<void> {
    const res = await request(itemPath(relPath), { method: 'DELETE' });
    if (!res.ok && res.status !== 404) throw await toError(res);
  },

  async listChildren(relPath: string): Promise<GraphItem[]> {
    // ルート直下は approot/children、それ以外は approot:/<path>:/children。
    // ルートに対してコロン付きの形を使うと 404 になり、
    // 「フォルダが1件も無い」と誤認して初回同期が空振りする。
    const select = '?$select=id,name,eTag,size,folder,file,lastModifiedDateTime';
    const url =
      relPath === '' ? `${APP_ROOT}/children${select}` : `${itemPath(relPath)}:/children${select}`;
    const res = await request(url);
    if (res.status === 404) return [];
    if (!res.ok) throw await toError(res);
    const body = (await res.json()) as { value: GraphItem[] };
    return body.value;
  },
};
